import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/authApi';
import { LoginRequest } from '@/types/auth';
import apiClient from '@/lib/api';

export const useLogin = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (credentials: LoginRequest) => authApi.login(credentials),
    onSuccess: (response) => {
      // Store auth data in localStorage for session persistence
      const { token, userId, role } = response;
      localStorage.setItem('authToken', token);
      localStorage.setItem('userRole', role);
      localStorage.setItem('userId', userId);
      
      // Update query cache with auth state
      queryClient.setQueryData(['auth'], { token, userId, role });
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId: string) => authApi.logout(userId),
    onSuccess: () => {
      // Clear localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      
      // Clear query cache
      queryClient.clear();
      
      // Redirect to home (handled by interceptor, but ensure here too)
      window.location.href = '/';
    },
  });
};

/**
 * Validates token with backend by making an authenticated request.
 * Returns true if token is valid, false otherwise.
 * Note: If token is invalid, the API interceptor will handle clearing auth state.
 */
const validateToken = async (userId: string): Promise<boolean> => {
  try {
    // Use user profile endpoint to validate token with longer timeout for cold starts
    // This endpoint requires authentication, so if token is invalid, it will return 400/401
    // Use a longer timeout (30s) for validation requests to handle Render cold starts
    await apiClient.get(`/api/users/${userId}`, {
      timeout: 30000, // 30 seconds for cold starts on Render
    });
    return true;
  } catch (error: any) {
    // Handle timeout errors gracefully (backend might be spinning up)
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      // Timeout error - assume token might still be valid, don't clear auth state
      // This handles cases where backend is spinning up from inactivity
      console.warn('Auth validation timeout - backend may be spinning up. Assuming token is valid.');
      return true;
    }
    
    // If we get 400 or 401, token is invalid
    // The API interceptor will handle clearing auth state and redirecting
    const status = error.response?.status;
    if (status === 400 || status === 401) {
      return false;
    }
    
    // For other errors (network, etc.), assume token might still be valid
    // Don't clear auth state on network errors - user might be offline
    console.warn('Auth validation error (non-timeout):', error.message || error);
    return true;
  }
};

export const useAuthState = () => {
  // Initialize from localStorage on mount
  const token = localStorage.getItem('authToken');
  const userId = localStorage.getItem('userId');
  const role = localStorage.getItem('userRole');
  const hasInitialAuth = !!(token && userId);
  
  return useQuery({
    queryKey: ['auth'],
    queryFn: async () => {
      const currentToken = localStorage.getItem('authToken');
      const currentUserId = localStorage.getItem('userId');
      const currentRole = localStorage.getItem('userRole');
      
      // If no token/userId, return null (not authenticated)
      if (!currentToken || !currentUserId) {
        return null;
      }
      
      // Validate token with backend on app load
      // This ensures we verify token validity with backend
      // Wrap in try-catch to handle any unexpected errors gracefully
      try {
        const isValid = await validateToken(currentUserId);
        
        if (!isValid) {
          // Token is invalid - return null
          // Note: API interceptor will handle clearing localStorage and redirecting
          return null;
        }
        
        // Token is valid - return auth state
        return {
          token: currentToken,
          userId: currentUserId,
          role: currentRole || '',
        };
      } catch (error: any) {
        // If validation throws an unexpected error, assume token is valid
        // This prevents uncaught promise rejections
        console.warn('Unexpected error during auth validation:', error);
        return {
          token: currentToken,
          userId: currentUserId,
          role: currentRole || '',
        };
      }
    },
    initialData: hasInitialAuth ? { token: token!, userId: userId!, role: role || '' } : undefined,
    staleTime: 5 * 60 * 1000, // Re-validate every 5 minutes
    retry: false, // Don't retry validation failures
    // Handle errors gracefully - don't throw uncaught promise rejections
    throwOnError: false,
  });
};
